// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled, useTheme } from '@mui/material/styles';
import {
  // CardItem,
  DatePicker,
  DatePickerValue,
  // ObjectiveKpiCard,
  SingleLineText,
  Tag,
  TitlePart,
  createLoadingShimmerStyles,
} from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import {
  // ANNUAL_GOAL_PLACEHOLDER,
  AnnualCurrentMonthHeaderState,
  isCurrentYear,
  referenceDateToYear,
  yearToReferenceDate,
} from '../../utils/annualPrime.utils';
import { formatPrimeEuro, formatPrimeEuroWithSign } from '../../utils/monthlyPrime.utils';

export type AnnualTamPrimesHeaderProps = {
  tamDisplayName: string;
  tamSeniority?: string;
  selectedYear: string;
  onSelectedYearChange: (year: string) => void;
  currentMonth?: AnnualCurrentMonthHeaderState | null;
  loading: boolean;
};

const AnnualTamPrimesHeader = ({
  tamDisplayName,
  tamSeniority,
  selectedYear,
  onSelectedYearChange,
  currentMonth,
  loading,
}: AnnualTamPrimesHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['primes']);

  const showCurrentMonthSection = isCurrentYear(selectedYear);
  // const goalPercent = ANNUAL_GOAL_PLACEHOLDER.percent;
  // const goalRemainingAmount = ANNUAL_GOAL_PLACEHOLDER.targetAmount - ANNUAL_GOAL_PLACEHOLDER.currentAmount;
  const formatAmount = (value: number | undefined) => (loading ? '...' : formatPrimeEuro(value ?? 0));
  const formatRegularisation = (value: number | undefined) => (loading ? '...' : formatPrimeEuroWithSign(value ?? 0));

  const currentMonthStatusTag =
    currentMonth?.status === 'validated'
      ? {
          text: t('monthStatusValidated'),
          backgroundColor: theme.palette.green[20],
          color: theme.palette.green[100],
        }
      : {
          text: t('monthStatusPending'),
          backgroundColor: theme.palette.beige[40],
          color: theme.palette.beige[100],
        };

  return (
    <HeaderContainer $withCurrentMonth={showCurrentMonthSection}>
      <IdentityBlock>
        <TitlePart textTitle={tamDisplayName || '—'} variant="heading6" />
        <MetaRow>
          <MetaLabel text={`${t('seniorityLabel')} :`} variant="body2Regular" />
          <Tag
            text={tamSeniority?.trim() || '—'}
            backgroundColor={theme.palette.beige[40]}
            color={theme.palette.beige[100]}
          />
        </MetaRow>
        <YearPickerRow>
          <MetaLabel text={t('annualYearLabel')} variant="body2Regular" />
          <DatePickerWrapper>
            <DatePicker
              views={['year']}
              openTo="year"
              value={yearToReferenceDate(selectedYear)}
              handleChange={(date: DatePickerValue) => {
                if (date) {
                  onSelectedYearChange(referenceDateToYear(date));
                }
              }}
            />
          </DatePickerWrapper>
        </YearPickerRow>
      </IdentityBlock>

      {showCurrentMonthSection ? (
        <>
          <VerticalDivider />
          <CurrentMonthBlock $loading={loading}>
            <MonthStatusRow>
              <TitlePart
                textTitle={currentMonth?.monthName || '—'}
                span={currentMonth?.year || selectedYear}
                variant="heading6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.neutral.black,
                  textAlign: 'left',
                  '& span': {
                    marginLeft: theme.spacing(1),
                    fontWeight: 400,
                    color: theme.palette.neutral[50],
                  },
                }}
              />
              {currentMonth ? (
                <>
                  <Tag
                    text={currentMonthStatusTag.text}
                    backgroundColor={currentMonthStatusTag.backgroundColor}
                    color={currentMonthStatusTag.color}
                  />
                  <AmountsColumn>
                    <AmountLine
                      text={t('annualCurrentMonthCommissionValue', {
                        amount: formatAmount(currentMonth.totalPrime),
                      })}
                      variant="body1Regular"
                    />
                    <AmountLine
                      text={t('annualCurrentMonthRegulValue', {
                        amount: formatRegularisation(currentMonth.regularisation),
                      })}
                      variant="body1Regular"
                      $positive={!loading && currentMonth.regularisation > 0}
                      $negative={!loading && currentMonth.regularisation < 0}
                    />
                    <AmountLine
                      text={t('annualCurrentMonthBonusValue', {
                        amount: formatAmount(currentMonth.bonus),
                      })}
                      variant="body1Regular"
                    />
                  </AmountsColumn>
                </>
              ) : null}
            </MonthStatusRow>
          </CurrentMonthBlock>
        </>
      ) : null}

      {/* Temporairement masqué — réactiver avec TEC-2816 (goalCompletion)
      <VerticalDivider />

      <GoalCardWrapper $loading={loading}>
        <ObjectiveKpiCard
          accent="blue"
          icon={<CardItem variant="blue" size="small" emoji={<InfoOutlinedIcon fontSize="small" />} />}
          title={t('annualHeaderGoalTitle')}
          valuePrimary={formatAmount(ANNUAL_GOAL_PLACEHOLDER.currentAmount)}
          valueSecondary={` / ${formatAmount(ANNUAL_GOAL_PLACEHOLDER.targetAmount)}`}
          progressPercent={goalPercent}
          footer={t('annualGoalProgressFooter', {
            remaining: formatAmount(goalRemainingAmount),
            percent: goalPercent,
          })}
        />
      </GoalCardWrapper>
      */}
    </HeaderContainer>
  );
};

export default AnnualTamPrimesHeader;

const HeaderContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$withCurrentMonth',
})<{ $withCurrentMonth: boolean }>(({ theme, $withCurrentMonth }) => ({
  display: 'grid',
  gridTemplateColumns: $withCurrentMonth ? 'minmax(180px, auto) 1px minmax(0, 1fr)' : 'minmax(180px, auto)',
  alignItems: 'center',
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(2),
  marginTop: theme.spacing(3),
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '16px',
  border: `1px solid ${theme.palette.neutral[20]}`,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const IdentityBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
}));

const MetaRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const YearPickerRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const MetaLabel = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral[60],
}));

const DatePickerWrapper = styled('div')(() => ({
  minWidth: '160px',
}));

const VerticalDivider = styled('div')(({ theme }) => ({
  width: '1px',
  alignSelf: 'stretch',
  backgroundColor: theme.palette.neutral[20],
  minHeight: '100%',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const CurrentMonthBlock = styled('div', {
  shouldForwardProp: (prop) => prop !== '$loading',
})<{ $loading: boolean }>(({ theme, $loading }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  minWidth: 0,
  ...createLoadingShimmerStyles({ active: $loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
}));

const MonthStatusRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const AmountsColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flexBasis: '100%',
  gap: theme.spacing(0.5),
}));

const AmountLine = styled(SingleLineText, {
  shouldForwardProp: (prop) => prop !== '$positive' && prop !== '$negative',
})<{ $positive?: boolean; $negative?: boolean }>(({ theme, $positive, $negative }) => ({
  color: $negative ? theme.palette.red[100] : $positive ? theme.palette.green[100] : theme.palette.neutral.black,
  fontWeight: 600,
}));

// const GoalCardWrapper = styled('div', {
//   shouldForwardProp: (prop) => prop !== '$loading',
// })<{ $loading: boolean }>(({ $loading }) => ({
//   minWidth: 0,
//   width: '100%',
//   '& > div': {
//     minWidth: 0,
//     width: '100%',
//   },
//   ...createLoadingShimmerStyles({ active: $loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
// }));
